import { useEffect, useState } from "react";
import type { CSSObject } from "@emotion/react";
import {
  IonButton,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TimeZoneDisclaimer } from "components/Molecules/TimezoneDisclaimer/TimezoneDisclaimer";
import { usePanelFlow } from "components/PanelFlow";
import { isDateSelectable, isSameDate } from "components/RequestForm/hooks";
import { DateTime, Settings } from "luxon";
import { IntervalModel, IssueKeyType, IssueType } from "model";
import { useAuth } from "pages";
import { Alert, Asterisk, CheckboxInput, Colors, TimeInput } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { IssueParam, IssueParamListItem, reportIssue, translate } from "~utils";

import { ReportIssueKey, ReportIssueModalProps } from "../types";
import { getLatestReportableInterval } from "../utils";
import { getPriorReport, hasPriorReport, hasPriorReportType } from "./helpers";

/***** Styles *****/
const styles: Record<string, CSSObject> = {
  label: { fontWeight: 500 },
  labelWithDatePicker: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

/***** Types *****/
// "no-show" here is for the limbo state where
// neither confirm nor not confirmed has been selected.
type SelectionReasonStrings = IssueKeyType | "no-show";

type ReportIssueFormProps = ReportIssueModalProps & {
  goToPanel: (panelKey: string) => void;
};

export function ReportIssueForm(props: ReportIssueFormProps) {
  const { state, onDismiss, extraProps, setState, goToPanel } = props;
  const { intervals, subName, status, requestId, school } = extraProps;

  // Set timezone BEFORE accessing any interval times
  Settings.defaultZone = school.tzString;
  const findInitialInterval = state?.initialIntervalId
    ? IntervalModel.getIntervalById(intervals, state.initialIntervalId)
    : undefined;
  // The logic for max allowed date is such that:
  //
  // * If the request is currently active, make the max selectable interval today.
  // * If the request is complete, make the max selectable interval the last day of the request.
  // * If an initial interval is set (arrived on this page via a specific report issue edit button),
  //   that date is set.
  const maxAllowedDate = findInitialInterval || getLatestReportableInterval(intervals);

  /********* Hooks ********/
  const { userInfo } = useAuth();
  const queryClient = useQueryClient();

  const [hasApiError, setHasApiError] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<IntervalModel>(maxAllowedDate);

  // The initial setting of these two values happen in the useEffect call.
  // An initial value has to be passed to useState because Typescript is
  // lovely like that.

  const [arrivedLateTime, setArrivedLateTime] = useState<DateTime>(selectedInterval.start);
  const [leftEarlyTime, setLeftEarlyTime] = useState<DateTime>(selectedInterval.end);
  const [selectedReasons, setSelectedReasons] = useState<SelectionReasonStrings[]>([]);
  const [leftEarlyApproved, setLeftEarlyApproved] = useState<boolean | null>(null);
  const [noShowConfirmed, setNoShowConfirmed] = useState<boolean | null>(null);
  const [details, setDetails] = useState<string>("");

  useEffect(() => {
    if (hasPriorReport(selectedInterval)) {
      // Details

      if (selectedInterval.comments) {
        setDetails(selectedInterval.comments);
      }

      const tempSelectedReasons: SelectionReasonStrings[] = [];

      // Sub Late
      if (hasPriorReportType(selectedInterval, IssueType.Late)) {
        const priorLateIssue = getPriorReport(selectedInterval, IssueType.Late);
        setArrivedLateTime(priorLateIssue.tardyTimestamp);
        tempSelectedReasons.push(IssueType.Late);
      }

      // Sub Lunch Late
      if (hasPriorReportType(selectedInterval, IssueType.LunchLate)) {
        tempSelectedReasons.push(IssueType.LunchLate);
      }

      if (
        hasPriorReportType(selectedInterval, IssueType.NoShowConfirmed) ||
        hasPriorReportType(selectedInterval, IssueType.NoShowApproved)
      ) {
        tempSelectedReasons.push("no-show");
        setNoShowConfirmed(true);
      }

      // Sub no show (unconfirmed - maps to false)
      if (hasPriorReportType(selectedInterval, IssueType.NoShowUnConfirmed)) {
        tempSelectedReasons.push("no-show");
        setNoShowConfirmed(false);
      }

      if (hasPriorReportType(selectedInterval, IssueType.SubLeftEarlyApproved)) {
        const priorLeftEarlyIssue = getPriorReport(
          selectedInterval,
          IssueType.SubLeftEarlyApproved,
        );
        setLeftEarlyTime(priorLeftEarlyIssue.tardyTimestamp);
        tempSelectedReasons.push(IssueType.SubLeftEarly);
        setLeftEarlyApproved(true);
      } else if (hasPriorReportType(selectedInterval, IssueType.SubLeftEarly)) {
        // Sub left early (unapproved - maps to false)
        const priorLeftEarlyIssue = getPriorReport(selectedInterval, IssueType.SubLeftEarly);
        setLeftEarlyTime(priorLeftEarlyIssue.tardyTimestamp);
        tempSelectedReasons.push(IssueType.SubLeftEarly);
        setLeftEarlyApproved(false);
      }

      // Sub urgent on campus concern
      if (hasPriorReportType(selectedInterval, IssueType.UrgentConcern)) {
        tempSelectedReasons.push(IssueType.UrgentConcern);
      }

      setSelectedReasons(tempSelectedReasons);
    } else {
      setSelectedReasons([]);
      setArrivedLateTime(selectedInterval.start);
      setLeftEarlyTime(selectedInterval.end);
    }
  }, [selectedInterval, school.tzString]);

  /********* Mutation ********/
  const { mutate: submitReportIssue } = useMutation({
    mutationFn: (submissionData: IssueParam) => {
      return reportIssue(requestId, submissionData);
    },
    onSuccess: () => {
      if (selectedReasons.includes("no-show")) {
        if (noShowConfirmed === true) {
          // Yes - sub gave advance notice → Approved (removes day)
          setState({ reportedIssues: [IssueType.NoShowApproved] });
        } else if (isSelectedDateInPast) {
          // No - past date → Confirmed (keeps day, shows issue)
          setState({ reportedIssues: [IssueType.NoShowConfirmed] });
        } else {
          // No - current/future date → Unconfirmed
          setState({ reportedIssues: [IssueType.NoShowUnConfirmed] });
        }
      } else if (selectedReasons.includes(IssueType.SubLeftEarly)) {
        setState({
          reportedIssues: [
            leftEarlyApproved ? IssueType.SubLeftEarlyApproved : IssueType.SubLeftEarly,
          ],
        });
      } else {
        setState({ reportedIssues: selectedReasons });
      }
      goToPanel(ReportIssueKey.ReportIssueConfirmPanel);
      queryClient.invalidateQueries();
    },
    onError: () => {
      setHasApiError(true);
    },
  });

  const findInterval = (dateString: string) => {
    return intervals.find((item) => {
      const originalStart = item.start;
      const selectedStart = DateTime.fromISO(dateString);
      return isSameDate(originalStart, selectedStart);
    });
  };

  // Check if selected interval is in the past (not today or future)
  const isSelectedDateInPast = selectedInterval.start < DateTime.now().startOf("day");

  function isButtonDisabled() {
    if (selectedReasons.includes("no-show")) {
      // No show requires selecting approved/unconfirmed for all requests
      // Radio button required for all requests (single-day and multi-day)
      if (noShowConfirmed === null) return true;

      // Details/comments are required only when "No" is selected
      if (noShowConfirmed === false && details.trim() === "") {
        return true;
      }
    }

    if (selectedReasons.includes(IssueType.SubLeftEarly)) {
      if (leftEarlyApproved === null) return true;
    }

    if (selectedInterval.issues.length >= 1) {
      // Allow issue to be deleted.
      return false;
    }

    if (selectedReasons.length >= 1) {
      return false;
    }

    return true;
  }

  function changeSelectedReasons(selection: SelectionReasonStrings) {
    if (selectedReasons.includes(selection)) {
      setSelectedReasons((old) => {
        const filteredArr = old.filter((item) => item !== selection);
        // Reset states when deselecting
        if (selection === "no-show") {
          setNoShowConfirmed(null);
          setDetails("");
        }
        if (selection === IssueType.SubLeftEarly) {
          setLeftEarlyApproved(null);
        }
        return filteredArr;
      });
      return;
    }
    if (selection === "no-show") {
      setSelectedReasons(["no-show"]);
      return;
    }

    setSelectedReasons((old) => {
      // If "no-show" was previously selected, and admin picks
      // a new selection, replace it w/ a new reason, but also
      // reset state
      if (old.includes("no-show")) {
        setNoShowConfirmed(null);
        setDetails("");
        return [selection];
      }
      return [...old, selection];
    });
  }

  function onReportSubmit() {
    const issueList = selectedReasons.map((item): IssueParamListItem => {
      switch (item) {
        case IssueType.Late:
          return {
            issue: IssueType.Late,
            "tardy-timestamp": arrivedLateTime.toISO(),
            "tardy-offset": Math.abs(arrivedLateTime.offset),
          };
        case IssueType.LunchLate:
          return {
            issue: IssueType.LunchLate,
          };
        case IssueType.SubLeftEarly:
          return {
            issue: leftEarlyApproved ? IssueType.SubLeftEarlyApproved : IssueType.SubLeftEarly,
            "tardy-timestamp": leftEarlyTime.toISO(),
            "tardy-offset": Math.abs(leftEarlyTime.offset),
          };
        case "no-show":
          if (noShowConfirmed === true) {
            // "Yes" selected → Always approved (removes day, no issue)
            return {
              issue: IssueType.NoShowApproved,
            };
          } else {
            // "No" selected
            if (isSelectedDateInPast) {
              // Past date → Confirmed no-show (keeps day, shows issue)
              return {
                issue: IssueType.NoShowConfirmed,
              };
            } else {
              // Current/Future date → Unconfirmed no-show
              return {
                issue: IssueType.NoShowUnConfirmed,
              };
            }
          }
        case IssueType.UrgentConcern:
          return {
            issue: IssueType.UrgentConcern,
          };
        default:
          return {
            issue: null,
          };
      }
    });

    const requestPayload: IssueParam = {
      "interval-id": selectedInterval.eid,
      comments: details,
      issues: issueList,
    };

    submitReportIssue(requestPayload);
  }

  /********* Render ********/
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("REPORT_ISSUE_FORM_HEADER")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      {hasApiError && (
        <div className="ion-margin-top">
          <Alert
            color="danger"
            onDismiss={() => setHasApiError(false)}
            showIcon
            title={translate("LABEL_SOMETHING_WENT_WRONG")}
            className="mx-4"
          >
            <span>
              {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={generateSwingSupportURL("/hc/en-us/requests/new")}
              >
                {translate("LABEL_CONTACT_SUPPORT")}
              </a>{" "}
              {translate("LABEL_CONTACT_SUPPORT_2")}
            </span>
          </Alert>
        </div>
      )}
      <IonContent
        css={
          hasApiError
            ? {
                "--offset-bottom": "auto !important",
                "--overflow": "auto",
                background:
                  "var(--ion-toolbar-background, var(--ion-background-color, #000000)) !important",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 0,
                  height: 0,
                },
              }
            : {}
        }
      >
        <IonList
          css={{
            minHeight: "100%",
            background: Colors.white200,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          // TODO: Remove this and move it into the css object
          className="ion-padding"
        >
          <div>
            <IonItem lines="none">{translate("REPORT_ISSUE_FORM_SUBHEADER", subName)}</IonItem>
            {userInfo?.isSuperAdmin && (
              <TimeZoneDisclaimer
                hideHeader
                isSuperAdmin={userInfo?.isSuperAdmin}
                schoolName={school.name}
                timeZone={school.tzString}
              />
            )}
            <IonItem disabled={intervals.length <= 1} lines="inset">
              <IonLabel className="font-medium">
                {translate("REPORT_ISSUE_FORM_SELECT_DAY")}
              </IonLabel>
              <IonDatetimeButton datetime="issueDay" />
              <IonModal keepContentsMounted className="stackedModal">
                <IonDatetime
                  className="lowercase"
                  showDefaultButtons
                  id="issueDay"
                  presentation="date"
                  min={intervals[0].start.toString()}
                  max={maxAllowedDate.start.toString()}
                  value={selectedInterval.start.toISO()}
                  isDateEnabled={isDateSelectable}
                  onIonChange={(newDate: CustomEvent) => {
                    const foundVal = findInterval(newDate.detail.value);
                    if (foundVal) {
                      setSelectedInterval(foundVal);
                    }
                  }}
                />
              </IonModal>
            </IonItem>
            <IonItem lines="none" css={styles.label}>
              <IonLabel>{translate("REPORT_ISSUE_FORM_SELECT_REASON")}</IonLabel>
            </IonItem>
            <CheckboxInput
              label={
                <div css={styles.labelWithDatePicker}>
                  {translate("REPORT_ISSUE_FORM_LATE")}
                  <TimeInput
                    min={selectedInterval.start}
                    value={arrivedLateTime}
                    isDisabled={!selectedReasons.includes(IssueType.Late)}
                    onChange={(newDate) => setArrivedLateTime(newDate)}
                    showTextInputOnDesktop
                  />
                </div>
              }
              isChecked={selectedReasons.includes(IssueType.Late)}
              onChange={() => changeSelectedReasons(IssueType.Late)}
              hasBorderBottom
            />
            <CheckboxInput
              label={translate("REPORT_ISSUE_FORM_LATE_LUNCH")}
              isChecked={selectedReasons.includes(IssueType.LunchLate)}
              onChange={() => changeSelectedReasons(IssueType.LunchLate)}
              hasBorderBottom
            />
            <CheckboxInput
              label={
                <div css={styles.labelWithDatePicker}>
                  {translate("REPORT_ISSUE_FORM_LEFT_EARLY")}
                  <TimeInput
                    max={selectedInterval.end}
                    value={leftEarlyTime}
                    isDisabled={!selectedReasons.includes(IssueType.SubLeftEarly)}
                    onChange={(newDate) => setLeftEarlyTime(newDate)}
                    showTextInputOnDesktop
                  />
                </div>
              }
              isChecked={selectedReasons.includes(IssueType.SubLeftEarly)}
              onChange={() => changeSelectedReasons(IssueType.SubLeftEarly)}
              hasBorderBottom={!selectedReasons.includes(IssueType.SubLeftEarly)}
            />
            {selectedReasons.includes(IssueType.SubLeftEarly) && (
              <div css={{ padding: "0 12px" }}>
                <IonItem>
                  <IonLabel css={styles.label}>
                    {`${translate("REPORT_ISSUE_FORM_LEFT_EARLY_LABEL")} `}
                    <Asterisk />
                  </IonLabel>
                </IonItem>
                <IonRadioGroup
                  onIonChange={(e) => setLeftEarlyApproved(e.target.value === "true")}
                  value={leftEarlyApproved === null ? undefined : String(leftEarlyApproved)}
                >
                  <IonItem>
                    <IonRadio labelPlacement="end" justify="start" value="true">
                      {translate("REPORT_ISSUE_FORM_LEFT_EARLY_YES")}
                    </IonRadio>
                  </IonItem>
                  <IonItem>
                    <IonRadio labelPlacement="end" justify="start" value="false">
                      {translate("REPORT_ISSUE_FORM_LEFT_EARLY_NO")}
                    </IonRadio>
                  </IonItem>
                </IonRadioGroup>
              </div>
            )}
            <CheckboxInput
              label={translate("REPORT_ISSUE_FORM_NO_SHOW")}
              isChecked={selectedReasons.includes("no-show")}
              onChange={() => changeSelectedReasons("no-show")}
              hasBorderBottom={!selectedReasons.includes("no-show")}
            />
            {selectedReasons.includes("no-show") && (
              <div css={{ padding: "0 12px" }}>
                <IonItem>
                  <IonLabel css={styles.label}>
                    {`${translate("REPORT_ISSUE_FORM_NO_SHOW_ADVANCE_NOTICE_LABEL")} `}
                    <Asterisk />
                  </IonLabel>
                </IonItem>

                <>
                  <IonRadioGroup
                    onIonChange={(e) => setNoShowConfirmed(e.target.value === "true")}
                    value={noShowConfirmed === null ? undefined : String(noShowConfirmed)}
                  >
                    <IonItem>
                      <IonRadio labelPlacement="end" justify="start" value="true">
                        {translate("LABEL_YES")}
                      </IonRadio>
                    </IonItem>
                    <IonItem>
                      <IonRadio labelPlacement="end" justify="start" value="false">
                        {translate("LABEL_NO")}
                      </IonRadio>
                    </IonItem>
                  </IonRadioGroup>
                  {/* Details field - always shown when no-show is selected */}
                  {/* Required for: "No" selected (unconfirmed) */}
                  {/* Not required for: "Yes" selected (approved/confirmed) */}
                  <IonItem lines="none">
                    <IonLabel css={styles.label}>
                      {translate("REPORT_ISSUE_FORM_DETAILS_LABEL")}
                      {noShowConfirmed === false && <Asterisk />}
                    </IonLabel>
                  </IonItem>
                  <IonItem>
                    <IonInput
                      aria-label={translate("REPORT_ISSUE_FORM_DETAILS_LABEL")}
                      placeholder={translate("REPORT_ISSUE_FORM_DETAILS_PLACEHOLDER")}
                      labelPlacement="stacked"
                      value={details}
                      onIonInput={(e) => setDetails(e.target.value?.toString() ?? "")}
                    />
                  </IonItem>
                </>
              </div>
            )}
            {/* Urgent Concern Option */}
            {/* Do NOT show if past request */}
            {/* See: https://linear.app/swing-education/issue/MKTP-962/%5Bfe%5D-remove-urgent-concern-option-from-past-requests */}
            {status !== "COMPLETED" && (
              <CheckboxInput
                label={translate("REPORT_ISSUE_FORM_URGENT_CONCERN")}
                isChecked={selectedReasons.includes(IssueType.UrgentConcern)}
                onChange={() => changeSelectedReasons(IssueType.UrgentConcern)}
                hasBorderBottom
              />
            )}
          </div>

          <div
            css={{
              padding: "16px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginTop: "auto",
            }}
          >
            <IonButton
              onClick={() => onReportSubmit()}
              expand="block"
              disabled={isButtonDisabled()}
            >
              {translate("LABEL_SUBMIT")}
            </IonButton>

            <IonButton onClick={() => onDismiss()} expand="block" fill="outline">
              {translate("LABEL_CANCEL")}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
}

export function ReportIssueFormPanel() {
  const panelFlowProps = usePanelFlow();
  const { extraProps, goToPanel, state, setState } = panelFlowProps;

  return (
    <ReportIssueForm
      state={state}
      extraProps={panelFlowProps.extraProps}
      onDismiss={extraProps.onDismiss}
      goToPanel={goToPanel}
      setState={setState}
    />
  );
}
